import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Building2, Globe, Link2, Lock, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { isValidEmail } from '@/utils/validation';
import { useStore } from '@/stores/useStore';
import { useSharing, type SharingResourceType } from '@/hooks/useSharing';
import { useShareLink } from '@/hooks/useShareLink';
import type { ShareRole, Visibility } from '@/api/generated/types.gen';
import { Spinner } from '@/components/ui/spinner';

interface ShareDialogProps {
  resourceType: SharingResourceType;
  resourceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Scope options for the General access Select, in Google-Drive order. */
const SCOPE_ORDER: Visibility[] = ['private', 'organization', 'public'];

/** Role options for every role Select, ordered least → most access. */
const ROLE_ORDER: ShareRole[] = ['viewer', 'commenter', 'editor'];

/**
 * Roles a *link* scope may grant, least → most access.
 *
 * "Anyone with the link" never grants edit: editing stays with the owner and
 * the people they invite by name (2026-07-22 dev sync). The backend rejects
 * that combination and caps it again at read time, so offering it here would
 * only produce a failed request. Per-person invites are unaffected — they keep
 * the full `ROLE_ORDER`.
 */
export function linkRoleOptions(scope: Visibility): ShareRole[] {
  return scope === 'public'
    ? ROLE_ORDER.filter((role) => role !== 'editor')
    : ROLE_ORDER;
}

/**
 * `role`, lowered to the most a `scope` link may grant.
 *
 * Two callers need this: rendering a row stored before the cap existed (whose
 * `link_role` may still be "editor", and would otherwise leave the Select
 * trigger blank), and switching scope to public while the current role is one
 * the new scope disallows.
 */
export function cappedLinkRole(scope: Visibility, role: ShareRole): ShareRole {
  const allowed = linkRoleOptions(scope);
  return allowed.includes(role) ? role : allowed[allowed.length - 1];
}

function scopeLabel(t: TFunction, scope: Visibility): string {
  switch (scope) {
    case 'private':
      return t('compCommon.shareDialog.scopeRestricted');
    case 'organization':
      return t('compCommon.shareDialog.scopeOrganization');
    case 'public':
      return t('compCommon.shareDialog.scopeAnyoneWithLink');
  }
}

function roleLabel(t: TFunction, role: ShareRole): string {
  switch (role) {
    case 'viewer':
      return t('compCommon.shareDialog.roleViewer');
    case 'commenter':
      return t('compCommon.shareDialog.roleCommenter');
    case 'editor':
      return t('compCommon.shareDialog.roleEditor');
  }
}

function ScopeIcon({
  scope,
  className,
}: {
  scope: Visibility;
  className?: string;
}) {
  if (scope === 'public') return <Globe className={className} />;
  if (scope === 'organization') return <Building2 className={className} />;
  return <Lock className={className} />;
}

/**
 * Google-Drive-style sharing dialog for a geometry or a study. Replaces the
 * old binary public/private toggle with per-person invites (viewer/
 * commenter/editor), a General access scope, and a copy-link action.
 *
 * Owner-only in v1 — callers only render the trigger that opens this dialog
 * when `my_role === 'owner'` (see `GeometryVisibilityControl` /
 * `StudyVisibilityControl`).
 */
export function ShareDialog({
  resourceType,
  resourceId,
  open,
  onOpenChange,
}: ShareDialogProps) {
  const { t } = useTranslation();
  const showToast = useStore((s) => s.showToast);
  const { copyShareLink } = useShareLink();
  const {
    settings,
    isLoading,
    setScope,
    isScopePending,
    sendInvite,
    isInvitePending,
    lastInviteResult,
    changeInviteRole,
    deleteInvite,
  } = useSharing(resourceType, resourceId, open);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<ShareRole>('viewer');
  const [emailError, setEmailError] = useState<string | null>(null);

  const visibility = settings?.visibility ?? 'private';
  const linkRole = settings?.link_role ?? 'viewer';
  const linkRoleChoices = linkRoleOptions(visibility);
  const shownLinkRole = cappedLinkRole(visibility, linkRole);

  const handleInvite = () => {
    const email = inviteEmail.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setEmailError(t('compCommon.shareDialog.invalidEmail'));
      return;
    }
    setEmailError(null);
    sendInvite(email, inviteRole);
    setInviteEmail('');
    setInviteRole('viewer');
  };

  // Surface the "saved but email couldn't send" caveat once per successful
  // invite. Keyed on the invite response identity (a fresh mutation result),
  // not a render-count guard, so this fires exactly once per invite.
  const [notedInviteEmail, setNotedInviteEmail] = useState<string | null>(null);
  if (
    lastInviteResult &&
    lastInviteResult.share.email !== notedInviteEmail &&
    !isInvitePending
  ) {
    setNotedInviteEmail(lastInviteResult.share.email);
    if (lastInviteResult.email_sent) {
      showToast(
        t('compCommon.shareDialog.toastInvited', {
          email: lastInviteResult.share.email,
        }),
      );
    } else {
      showToast(
        t('compCommon.shareDialog.toastInvitedEmailFailed', {
          email: lastInviteResult.share.email,
        }),
      );
    }
  }

  const handleCopyLink = () => {
    copyShareLink(
      resourceType === 'geometry'
        ? { type: 'geometry', id: resourceId }
        : { type: 'study', id: resourceId },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">
            {t('compCommon.shareDialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('compCommon.shareDialog.description')}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Spinner className="size-5 text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* People with access */}
            <section className="space-y-2">
              <h3 className="text-xs font-medium text-muted-foreground">
                {t('compCommon.shareDialog.peopleWithAccess')}
              </h3>

              <div className="space-y-1.5">
                {settings?.owner_email && (
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-foreground">
                      {settings.owner_email}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {t('compCommon.shareDialog.roleOwner')}
                    </span>
                  </div>
                )}

                {settings?.shares.map((share) => (
                  <div
                    key={share.email}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                      {share.email}
                      {!share.user_known && (
                        <span className="ml-1.5 text-xs text-muted-foreground">
                          {t('compCommon.shareDialog.pendingInvite')}
                        </span>
                      )}
                    </span>
                    <Select
                      value={share.role}
                      onValueChange={(value) =>
                        changeInviteRole(share.email, value as ShareRole)
                      }
                    >
                      <SelectTrigger className="h-8 w-[110px] shrink-0 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ROLE_ORDER.map((role) => (
                          <SelectItem key={role} value={role}>
                            {roleLabel(t, role)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label={t('compCommon.shareDialog.removeAccess', {
                        email: share.email,
                      })}
                      onClick={() => deleteInvite(share.email)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Invite row */}
              <div className="flex items-start gap-1.5 pt-1">
                <div className="min-w-0 flex-1">
                  <Input
                    type="email"
                    placeholder={t('compCommon.shareDialog.invitePlaceholder')}
                    value={inviteEmail}
                    onChange={(e) => {
                      setInviteEmail(e.target.value);
                      if (emailError) setEmailError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleInvite();
                      }
                    }}
                    className="h-8 text-xs"
                    aria-invalid={emailError != null}
                  />
                  {emailError && (
                    <p className="mt-1 text-xs text-destructive">
                      {emailError}
                    </p>
                  )}
                </div>
                <Select
                  value={inviteRole}
                  onValueChange={(value) => setInviteRole(value as ShareRole)}
                >
                  <SelectTrigger className="h-8 w-[110px] shrink-0 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_ORDER.map((role) => (
                      <SelectItem key={role} value={role}>
                        {roleLabel(t, role)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="h-8 shrink-0"
                  disabled={!inviteEmail.trim() || isInvitePending}
                  onClick={handleInvite}
                >
                  {isInvitePending && (
                    <Spinner aria-hidden className="size-3.5" />
                  )}
                  {t('compCommon.shareDialog.invite')}
                </Button>
              </div>
            </section>

            {/* General access */}
            <section className="space-y-2 border-t border-border pt-4">
              <h3 className="text-xs font-medium text-muted-foreground">
                {t('compCommon.shareDialog.generalAccess')}
              </h3>
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                  <ScopeIcon
                    scope={visibility}
                    className="h-4 w-4 text-muted-foreground"
                  />
                </div>
                <Select
                  value={visibility}
                  onValueChange={(value) => {
                    // Switching org(editor) → public would otherwise send a
                    // combination the backend now rejects, so carry the role
                    // down to the most the new scope allows.
                    const scope = value as Visibility;
                    setScope(scope, cappedLinkRole(scope, linkRole));
                  }}
                  disabled={isScopePending}
                >
                  <SelectTrigger className="h-9 flex-1 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SCOPE_ORDER.map((scope) => (
                      <SelectItem key={scope} value={scope}>
                        {scopeLabel(t, scope)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {visibility !== 'private' && (
                  <Select
                    value={shownLinkRole}
                    onValueChange={(value) =>
                      setScope(visibility, value as ShareRole)
                    }
                    disabled={isScopePending}
                  >
                    <SelectTrigger className="h-9 w-[110px] shrink-0 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {linkRoleChoices.map((role) => (
                        <SelectItem key={role} value={role}>
                          {roleLabel(t, role)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {visibility === 'private' &&
                  t('compCommon.shareDialog.scopeRestrictedHint')}
                {visibility === 'organization' &&
                  t('compCommon.shareDialog.scopeOrganizationHint')}
                {visibility === 'public' &&
                  t('compCommon.shareDialog.scopeAnyoneWithLinkHint')}
              </p>
            </section>
          </div>
        )}

        <DialogFooter className="mt-2 sm:justify-start">
          <Button variant="outline" size="sm" onClick={handleCopyLink}>
            <Link2 className="h-3.5 w-3.5" />
            {t('compCommon.shareDialog.copyLink')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
